import type { HydraulicSystem } from '@shared/hydraulic'

export type NewPipePhase =
  | { ok: true; type: 'pipe' | 'compressible_pipe'; fluidZoneId: string | undefined }
  | { ok: false; reason: 'mixed-phase' }

const isCompressible = (edge: unknown): boolean =>
  (edge as { type?: string } | undefined)?.type === 'compressible_pipe'

const zoneOf = (edge: unknown): string | undefined =>
  (edge as { fluid_zone_id?: string } | undefined)?.fluid_zone_id

/** Any edge incident on `vertexId` — the run that vertex already sits on. */
function adjacentEdge(system: HydraulicSystem, vertexId: string): unknown {
  return system.edges.find(e => e.connection.source_id === vertexId || e.connection.target_id === vertexId)
}

/**
 * The `type` and `fluid_zone_id` a newly drawn pipe should take.
 *
 * ⚠️ A new pipe must NOT blindly emit an incompressible `pipe` in the model's default
 * zone. Teeing onto a compressible run that way silently creates a **mixed-phase
 * junction** — an incompressible pipe meeting gas pipes at one node.
 *
 * ⚠️ **A pipe with a bare-junction terminal stays incompressible.** A degree-1 dead-leg
 * is fine for the incompressible solver, but the compressible solver's behaviour on a
 * degree-1 `compressible_pipe` dead-leg is unverified. Rather than commit an unverified
 * gas dead-leg *or* a silent phase mix, this **refuses** when the two rules collide —
 * i.e. when the pipe would be forced incompressible while either end touches a
 * compressible run. Refusing costs only a case that was going to be physically wrong
 * either way, and keeps "never silently mix phases" absolute.
 *
 * If the compressible dead-leg case is ever confirmed working, the refusal becomes plain
 * inheritance and this comment is the place to start.
 */
export function resolveNewPipePhase(
  system: HydraulicSystem,
  args: { sourceId: string; targetEdgeId?: string | undefined; farEndIsBareJunction: boolean },
): NewPipePhase {
  const sourceRun = adjacentEdge(system, args.sourceId)
  const targetRun = args.targetEdgeId !== undefined
    ? system.edges.find(e => e.id === args.targetEdgeId)
    : undefined

  const sourceGas = sourceRun !== undefined && isCompressible(sourceRun)
  const targetGas = targetRun !== undefined && isCompressible(targetRun)

  // Forced incompressible by the bare-terminal rule, but touching gas at either end.
  if (args.farEndIsBareJunction && (sourceGas || targetGas)) {
    return { ok: false, reason: 'mixed-phase' }
  }

  // Both ends real but disagreeing on phase — inheriting either creates the mixed-phase
  // junction at the other.
  if (!args.farEndIsBareJunction && targetRun !== undefined && sourceRun !== undefined
      && sourceGas !== targetGas) {
    return { ok: false, reason: 'mixed-phase' }
  }

  // Inherit from the run this pipe actually joins: the tee target when there is one,
  // else the source's own run.
  const inheritFrom = targetRun ?? sourceRun
  return {
    ok: true,
    type: isCompressible(inheritFrom) ? 'compressible_pipe' : 'pipe',
    fluidZoneId: zoneOf(inheritFrom),
  }
}
